import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { checkPaymentStatus } from "../utils/phonepePayment";
import { toast } from "react-toastify";

function PaymentRedirect() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("Verifying your payment...");

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Get payment details from localStorage
      const pendingPaymentStr = localStorage.getItem("pendingPayment");

      if (!pendingPaymentStr) {
        setStatus("error");
        setMessage("Payment information not found");
        setTimeout(() => navigate("/portal/subscription"), 3000);
        return;
      }

      const pendingPayment = JSON.parse(pendingPaymentStr);
      const { merchantTransactionId, amount, duration, category, planType, userId } = pendingPayment;

      // Check payment status with PhonePe
      const paymentResult = await checkPaymentStatus(merchantTransactionId);

      if (paymentResult.success) {
        // Payment successful - save to Firebase
        await saveSubscriptionToFirebase(
          userId,
          merchantTransactionId,
          paymentResult.xVerify,
          amount,
          duration,
          category,
          planType
        );

        setStatus("success");
        setMessage("Payment successful! Your subscription has been activated.");

        // Clear pending payment
        localStorage.removeItem("pendingPayment");

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate("/portal/Dashboard");
        }, 3000);
      } else {
        setStatus("error");
        setMessage(paymentResult.error || "Payment verification failed");

        // Clear pending payment
        localStorage.removeItem("pendingPayment");

        // Redirect to subscription page after 3 seconds
        setTimeout(() => {
          navigate("/portal/subscription");
        }, 3000);
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      setStatus("error");
      setMessage("Failed to verify payment. Please contact support.");

      setTimeout(() => {
        navigate("/portal/subscription");
      }, 3000);
    }
  };

  const saveSubscriptionToFirebase = async (
    userId,
    transactionId,
    xVerify,
    amount,
    duration,
    category,
    planType
  ) => {
    try {
      const subscriptionData = {
        transactionId,
        "x-verify": xVerify,
        status: "paid",
        "start-time": new Date(),
        amount,
        type: "paid",
        category,
        duration,
        "end-time": new Date(Date.now() + duration * 30 * 24 * 60 * 60 * 1000), // duration in months
        platform: "WEB",
        planType,
      };

      const subscriptionRef = doc(db, "subscriptions", userId);
      const subscriptionDoc = await getDoc(subscriptionRef);

      if (subscriptionDoc.exists()) {
        // Update existing document
        await updateDoc(subscriptionRef, {
          transactions: arrayUnion(subscriptionData),
        });
      } else {
        // Create new document
        await setDoc(subscriptionRef, {
          transactions: [subscriptionData],
        });
      }

      console.log("Subscription saved to Firebase successfully");
    } catch (error) {
      console.error("Error saving subscription to Firebase:", error);
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-[#050125] flex items-center justify-center p-4">
      <div className="bg-[#1E1E45] rounded-3xl border-2 border-white p-8 max-w-md w-full text-center">
        {/* Loading Animation */}
        {status === "verifying" && (
          <div className="mb-6">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500"></div>
          </div>
        )}

        {/* Success Icon */}
        {status === "success" && (
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
        )}

        {/* Error Icon */}
        {status === "error" && (
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
        )}

        {/* Message */}
        <h2
          className={`text-2xl font-bold mb-4 ${
            status === "success"
              ? "text-green-500"
              : status === "error"
              ? "text-red-500"
              : "text-yellow-500"
          }`}
        >
          {status === "verifying"
            ? "Processing Payment"
            : status === "success"
            ? "Payment Successful"
            : "Payment Failed"}
        </h2>

        <p className="text-white text-lg mb-6">{message}</p>

        {/* Redirect Info */}
        <p className="text-gray-400 text-sm">
          {status === "success"
            ? "Redirecting to dashboard..."
            : "Redirecting to subscription page..."}
        </p>
      </div>
    </div>
  );
}

export default PaymentRedirect;
